import { api } from '../core/client';
import { wirePhone } from '../core/wire';

/** A wallet top-up through its staff-confirmed lifecycle. */
export interface ApiWalletTopup {
  id: string;
  phone: string;
  name: string;
  amount: number;
  method: string;
  /** requested | approved | rejected. */
  status: 'requested' | 'approved' | 'rejected';
  requestedAt: string;
  approvedAt: string;
}

interface ApiEnvelope<T> {
  success: boolean;
  message: string | null;
  result: T;
}

/**
 * Wallet top-ups. The app has no payment gateway, so a top-up is a REQUEST that credits
 * nothing until staff confirm the cash at the counter (approve) — that's the only path
 * that grows the wallet balance. This replaced the old client-only "add money" that minted
 * balance for free.
 */
export const walletService = {
  /** Customer asks to top up (status → requested). No balance change. */
  requestTopup(phone: string, amount: number, method: string, name = ''): Promise<ApiWalletTopup> {
    return api
      .post<ApiEnvelope<ApiWalletTopup>>('/wallet/topup', { phone: wirePhone(phone), amount, method, name })
      .then((r) => r.data.result);
  },

  /** The signed-in customer's own top-up requests (newest first). */
  myTopups(phone: string): Promise<ApiWalletTopup[]> {
    return api
      .get<ApiEnvelope<ApiWalletTopup[]>>('/wallet/topup/mine', { params: { phone: wirePhone(phone) } })
      .then((r) => r.data.result ?? [])
      .catch(() => []);
  },

  /** The panel approval queue — every pending top-up. */
  pending(): Promise<ApiWalletTopup[]> {
    return api.get<ApiEnvelope<ApiWalletTopup[]>>('/wallet/topup/requests').then((r) => r.data.result ?? []);
  },

  /** Staff confirm the cash (status → approved) — credits the wallet. */
  approve(id: string): Promise<void> {
    return api.post(`/wallet/topup/${id}/approve`, {}).then(() => undefined);
  },

  /** Staff decline the top-up (status → rejected). */
  reject(id: string): Promise<void> {
    return api.post(`/wallet/topup/${id}/reject`, {}).then(() => undefined);
  }
};
